import { Box, Divider, Paper, Tooltip, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";

export type KPIThresholds = {
	red: number;
	orange: number;
	yellow: number;
	greenSoft: number;
	greenNormal: number;
	greenHard: number;
};

export const SHIFT_CONFIG_COLORS = {
	red: "#F44336",
	orange: "#FF9800",
	yellow: "#FFC107",
	greenSoft: "#8BC34A",
	greenNormal: "#4CAF50",
	greenHard: "#2E7D32",
};

type Props = {
	title: string;
	value: number | null;
	unit: string;
	percentage: number | null;
	subtitle?: string;
	tooltip?: string;
	details: {
		label: string;
		value: string;
		isNegative?: boolean;
		tooltip?: string;
	}[];
	thresholds: KPIThresholds;
};

const getValueColor = (value: number, thresholds: KPIThresholds): string => {
	if (value >= thresholds.greenHard) return SHIFT_CONFIG_COLORS.greenHard;
	if (value >= thresholds.greenNormal) return SHIFT_CONFIG_COLORS.greenNormal;
	if (value >= thresholds.greenSoft) return SHIFT_CONFIG_COLORS.greenSoft;
	if (value >= thresholds.yellow) return SHIFT_CONFIG_COLORS.yellow;
	if (value >= thresholds.orange) return SHIFT_CONFIG_COLORS.orange;
	return SHIFT_CONFIG_COLORS.red;
};

export const KPICard = ({
	title,
	value,
	unit,
	percentage,
	subtitle,
	tooltip,
	details,
	thresholds,
}: Props) => {
	const isNullValue = value === null;
	const safeValue = value ?? 0;
	const safePct = percentage ?? 0;
	const valueColor = isNullValue ? "#999" : getValueColor(safePct, thresholds);

	const gaugeOptions = useMemo(() => {
		const maxValue = thresholds.greenHard || 100;
		const orangeStop = (thresholds.orange || 20) / maxValue;
		const yellowStop = (thresholds.yellow || 40) / maxValue;
		const greenSoftStop = (thresholds.greenSoft || 60) / maxValue;
		const greenNormalStop = (thresholds.greenNormal || 80) / maxValue;
		const greenHardStop = (greenNormalStop + 1) / 2;

		return {
			tooltip: { show: false },
			series: [
				{
					type: "gauge",
					silent: true,
					startAngle: 225,
					endAngle: 15,
					min: 0,
					max: maxValue,
					radius: "100%",
					center: ["50%", "55%"],
					axisLine: {
						lineStyle: {
							width: 15,
							color: [
								[orangeStop, SHIFT_CONFIG_COLORS.red],
								[yellowStop, SHIFT_CONFIG_COLORS.orange],
								[greenSoftStop, SHIFT_CONFIG_COLORS.yellow],
								[greenNormalStop, SHIFT_CONFIG_COLORS.greenSoft],
								[greenHardStop, SHIFT_CONFIG_COLORS.greenNormal],
								[1, SHIFT_CONFIG_COLORS.greenHard],
							],
						},
					},
					pointer: {
						icon: "triangle",
						length: "90%",
						width: 6,
						offsetCenter: [0, 0],
						itemStyle: {
							color: "#333",
						},
					},
					anchor: {
						show: true,
						showAbove: true,
						size: 10,
						itemStyle: {
							borderWidth: 2,
							borderColor: "#333",
							color: "#fff",
						},
					},
					axisTick: { show: false },
					splitLine: { show: false },
					axisLabel: { show: false },
					title: { show: false },
					detail: { show: false },
					data: [{ value: Math.min(safePct, maxValue) }],
				},
			],
		};
	}, [safePct, thresholds]);

	return (
		<Paper
			elevation={0}
			sx={{
				p: 0.75,
				height: "100%",
				display: "flex",
				flexDirection: "column",
				border: "1px solid #ddd",
				borderRadius: 1,
				backgroundColor: "#fff",
				userSelect: "none",
			}}
		>
			<Typography
				sx={{
					fontWeight: 700,
					color: "#333",
					fontSize: "0.85rem",
					lineHeight: 1,
					mb: 0.4,
				}}
			>
				{title}
			</Typography>

			<Box
				sx={{
					display: "flex",
					alignItems: "stretch",
					gap: 1,
					flex: 1,
					minHeight: 0,
				}}
			>
				<Box
					sx={{
						flex: "1 1 55%",
						minWidth: 0,
						position: "relative",
						minHeight: 110,
					}}
				>
					<ReactECharts
						option={gaugeOptions}
						style={{ height: "100%", width: "100%" }}
						opts={{ renderer: "svg" }}
					/>

					<Tooltip
						title={tooltip ?? ""}
						placement="bottom"
						disableHoverListener={!tooltip}
					>
						<Box
							display="flex"
							flexDirection="column"
							sx={{
								position: "absolute",
								top: "65%",
								left: "60%",
								transform: "translate(-50%, 10%)",
								display: "flex",
								alignItems: "flex-end",
								userSelect: "none",
							}}
						>
							<Box display="flex" alignItems="flex-end" gap={0.5}>
								<Typography
									sx={{
										fontSize: "1.25rem",
										fontWeight: 700,
										color: valueColor,
										lineHeight: 1,
									}}
								>
									{isNullValue ? "-" : parseFloat(safeValue.toFixed(2))}
								</Typography>
								<Typography
									sx={{
										fontSize: "0.65rem",
										fontWeight: 600,
										color: valueColor,
									}}
								>
									{unit}
								</Typography>
							</Box>

							{subtitle && (
								<Box sx={{ ml: 0.5, mt: 0.6, textAlign: "left" }}>
									<Typography
										sx={{
											fontSize: "0.6rem",
											color: "#666",
											lineHeight: 1.2,
											whiteSpace: "nowrap",
										}}
									>
										{subtitle}
									</Typography>
								</Box>
							)}
						</Box>
					</Tooltip>
				</Box>

				<Divider orientation="vertical" flexItem />

				<Box
					sx={{
						flex: "1 1 45%",
						minWidth: 0,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						gap: 0.5,
					}}
				>
					{details.map((detail) => (
						<Tooltip
							key={detail.label}
							title={detail.tooltip ?? ""}
							arrow
							placement="left"
							disableHoverListener={!detail.tooltip}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "baseline",
									justifyContent: "space-between",
									gap: 0.75,
									userSelect: "none",
								}}
							>
								<Typography
									sx={{
										fontSize: "0.7rem",
										color: "#666",
										whiteSpace: "nowrap",
									}}
								>
									{detail.label}
								</Typography>
								<Typography
									sx={{
										fontSize: "0.85rem",
										fontWeight: 700,
										color: detail.isNegative ? "#F44336" : "#222",
										fontVariantNumeric: "tabular-nums",
									}}
								>
									{typeof detail.value === "string" &&
									detail.value.includes("%")
										? `${parseFloat(parseFloat(detail.value).toFixed(2)) || "0"}%`
										: detail.value}
								</Typography>
							</Box>
						</Tooltip>
					))}
				</Box>
			</Box>
		</Paper>
	);
};
